import { Injectable } from '@angular/core';

/**
 * Interface para as opções de exportação CSV
 */
interface ExportCSVOptions {
  separator?: string;
  charset?: string;
  dateFormat?: string;
  includeHeader?: boolean;
}

/**
 * Serviço para exportação de dados em formato CSV
 */
@Injectable()
export class ExportCSVService {
  // Propriedades de controle do documento
  private nomeArquivo: string = '';
  private dados: string[] = [];
  private options: ExportCSVOptions = {
    separator: ';',
    charset: 'utf-8',
    dateFormat: 'yyyyMMdd-HHmm',
    includeHeader: true
  };

  /**
   * Inicializa um novo documento CSV
   */
  public novoDocumento(): void {
    this.dados = [];
  }

  /**
   * Define opções para a exportação
   * @param options Opções de configuração da exportação
   */
  public setOptions(options: ExportCSVOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Adiciona uma linha em branco ao documento
   */
  public pulaLinha(): void {
    this.dados.push('');
  }

  /**
   * Adiciona uma linha de texto ao documento
   * @param texto Texto a ser adicionado
   */
  public escreveTexto(texto: string): void {
    this.dados.push(texto);
  }

  /**
   * Adiciona uma tabela de dados ao documento
   * @param cabecalho Array com os títulos das colunas
   * @param data Array de arrays com os dados das linhas
   */
  public escreveTabela(cabecalho: string[], data: Array<string[]>): void {
    // Adiciona o cabeçalho se configurado
    if (this.options.includeHeader) {
      this.escreveTexto(cabecalho.join(this.options.separator));
    }

    // Adiciona as linhas de dados
    data.forEach((linha: string[]) => {
      this.escreveTexto(linha.join(this.options.separator));
    });
  }

  /**
   * Adiciona um objeto JavaScript ao documento
   * @param objeto Objeto a ser convertido em CSV
   * @param includeHeader Se deve incluir os nomes das propriedades como cabeçalho
   */
  public escreveObjeto(objeto: any, includeHeader: boolean = true): void {
    if (!objeto) {
      return;
    }

    // Se for um array de objetos
    if (Array.isArray(objeto) && objeto.length > 0) {
      // Extrai as chaves do primeiro objeto para o cabeçalho
      const cabecalho = Object.keys(objeto[0]);
      
      // Adiciona o cabeçalho se solicitado
      if (includeHeader) {
        this.escreveTexto(cabecalho.join(this.options.separator));
      }
      
      // Adiciona cada objeto como uma linha
      objeto.forEach(item => {
        const valores = cabecalho.map(chave => this.formatarValor(item[chave]));
        this.escreveTexto(valores.join(this.options.separator));
      });
    } 
    // Se for um único objeto
    else if (typeof objeto === 'object') {
      const chaves = Object.keys(objeto);
      
      // Adiciona o cabeçalho se solicitado
      if (includeHeader) {
        this.escreveTexto(chaves.join(this.options.separator));
      }
      
      // Adiciona os valores como uma linha
      const valores = chaves.map(chave => this.formatarValor(objeto[chave]));
      this.escreveTexto(valores.join(this.options.separator));
    }
  }

  /**
   * Define o nome do arquivo a ser salvo
   * @param nome Nome base do arquivo
   */
  public defineNomeArquivo(nome: string): void {
    const data = new Date();
    const timestamp = this.formatarDataHora(data, this.options.dateFormat);
    this.nomeArquivo = `${nome}-${timestamp}.csv`;
  }

  /**
   * Salva o documento para download pelo usuário
   * @param nomeArquivo Nome base do arquivo (opcional)
   */
  public salva(nomeArquivo?: string): void {
    if (nomeArquivo) {
      this.defineNomeArquivo(nomeArquivo);
    }

    // Prepara os dados para o download
    const saida = this.dados.join('\n');
    
    // Cria o blob final ja com o encoding esperado pelo Excel
    const blob = this.converterParaCSV(saida);

    // Cria a URL do arquivo
    const url = window.URL.createObjectURL(blob);

    // Cria um elemento de anchor para o download
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = this.nomeArquivo;
    
    // Adiciona ao DOM, realiza o clique e remove
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 100);
  }

  /**
   * Formata uma data conforme o padrão especificado
   * @param data Data a ser formatada
   * @param formato Formato de saída
   * @returns String formatada
   */
  private formatarDataHora(data: Date, formato: string): string {
    // Extrai os componentes da data
    const dia = ('0' + data.getDate()).slice(-2);
    const mes = ('0' + (data.getMonth() + 1)).slice(-2);
    const ano = data.getFullYear().toString();
    const horas = ('0' + data.getHours()).slice(-2);
    const minutos = ('0' + data.getMinutes()).slice(-2);
    const segundos = ('0' + data.getSeconds()).slice(-2);
    
    // Substitui os marcadores no formato
    let resultado = formato
      .replace('yyyy', ano)
      .replace('MM', mes)
      .replace('dd', dia)
      .replace('HH', horas)
      .replace('mm', minutos)
      .replace('ss', segundos);
      
    return resultado;
  }

  /**
   * Formata um valor para ser incluído no CSV
   * @param valor Valor a ser formatado
   * @returns String formatada
   */
  private formatarValor(valor: any): string {
    if (valor === null || valor === undefined) {
      return '';
    }
    
    if (valor instanceof Date) {
      return this.formatarDataHora(valor, this.options.dateFormat);
    }
    
    if (typeof valor === 'string') {
      // Escapa aspas duplas e envolve em aspas se tiver o separador
      if (valor.includes(this.options.separator) || valor.includes('"') || valor.includes('\n')) {
        return `"${valor.replace(/"/g, '""')}"`;
      }
      return valor;
    }
    
    return String(valor);
  }

  /**
   * Converte o texto para bytes CSV com o charset correto
   * @param texto Texto a ser convertido
   * @returns Blob com o conteúdo CSV
   */
  private converterParaCSV(texto: string): Blob {
    // Para UTF-8, a conversão é direta
    if (this.options.charset.toLowerCase() === 'utf-8') {
      return new Blob(['\ufeff', texto], { type: 'text/csv;charset=utf-8' });
    }
    
    // Para ANSI/Windows-1252, precisamos converter manualmente
    if (this.options.charset.toLowerCase() === 'ansi' || 
        this.options.charset.toLowerCase() === 'windows-1252') {
      const uint8 = new Uint8Array(texto.length);
      for (let i = 0; i < uint8.length; i++) {
        uint8[i] = texto.charCodeAt(i);
      }
      return new Blob([uint8], { type: `text/csv;charset=windows-1252` });
    }
    
    // Para outros formatos, usa UTF-8 como padrão
    return new Blob([texto], { type: `text/csv;charset=${this.options.charset}` });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/exportCSV.service.ts